import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';

export function HomePage() {
  const navigate = useNavigate();
  const [ffId, setFfId] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submissionId, setSubmissionId] = useState<string | null>(null);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, []);

  const handleFileSelect = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large (max 10MB)');
      return;
    }
    setScreenshot(file);
    setPreview(URL.createObjectURL(file));
    setError('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!ffId.trim()) {
      setError('Please enter your FF ID');
      setLoading(false);
      return;
    }

    if (!screenshot) {
      setError('Please select a screenshot');
      setLoading(false);
      return;
    }

    try {
      const result = await api.upload(ffId.trim(), screenshot);
      
      if (result.success && result.submissionId) {
        setSubmissionId(result.submissionId);
        setSuccess(true);
        setTimeout(() => {
          navigate(`/status/${ffId.trim()}`);
        }, 2000);
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch (err: any) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="card">
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: '800', color: 'var(--text)', marginBottom: '0.5rem' }}>
            Submit Screenshot
          </h1>
          <p style={{ color: 'var(--text-muted)' }}>
            Upload your FF ID screenshot for unban review
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="ffId" className="form-label">Free Fire ID</label>
            <input
              id="ffId"
              type="text"
              value={ffId}
              onChange={(e) => setFfId(e.target.value)}
              placeholder="Enter your FF ID (e.g., 123456789)"
              maxLength={20}
              required
              disabled={loading}
              autoComplete="off"
            />
            {error && <div className="form-error">{error}</div>}
          </div>

          <div className="form-group">
            <label className="form-label">Screenshot</label>
            <div
              className={`file-upload ${dragActive ? 'drag-active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                disabled={loading}
                style={{ display: 'none' }}
              />
              {preview ? (
                <>
                  <img src={preview} alt="Preview" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: 'var(--radius)' }} />
                  <p style={{ marginTop: '0.5rem', color: 'var(--text-muted)' }}>
                    {screenshot?.name} ({(screenshot!.size / 1024 / 1024).toFixed(2)} MB)
                  </p>
                </>
              ) : (
                <>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <polyline points="21 15 16 10 5 21"/>
                  </svg>
                  <p style={{ color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
                    Drag & drop your screenshot here, or click to browse
                  </p>
                  <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
                    PNG, JPG up to 10MB
                  </p>
                </>
              )}
            </div>
            {error && <div className="form-error">{error}</div>}
          </div>

          <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={loading || !ffId.trim() || !screenshot}>
            {loading ? (
              <>
                <span className="spinner" />
                Submitting...
              </>
            ) : (
              'Submit for Review'
            )}
          </button>
        </form>

        {success && submissionId && (
          <div style={{ marginTop: '1.5rem', padding: '1rem', background: 'var(--success-light)', borderRadius: 'var(--radius)', textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', color: 'var(--success)' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
              <span style={{ fontWeight: '600' }}>Submitted successfully!</span>
            </div>
            <p style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--text-muted)' }}>
              Redirecting to status page...
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const fileInputRef = React.createRef<HTMLInputElement>();